import { ToastStatusGlobal } from '@/components';
import { ProcessExemptionCopayPayload } from '@/modules/copay/entities/interfaces/copaies';
import {
  UsageStatement,
  UsageStatementProcess,
} from '@/modules/copay/entities/interfaces/usageStatements';
import { CopayService } from '@/modules/copay/services/copayService';
import { tryOrCatchMessageError } from '@/utils/tryOrCatchMessageError';
import { useCallback, useState } from 'react';

const copayService = new CopayService();

interface UseUsageStatementProps {
  callbackProcessSuccess?: (
    payload: ProcessExemptionCopayPayload,
    usageStatementProcess: UsageStatementProcess,
  ) => void;
  callbackPublishSuccess?: () => void;
  callbackUpdateSuccess?: () => void;
}

export const useUsageStatementProcess = (options?: UseUsageStatementProps) => {
  const [loading, setLoading] = useState(false);
  const { toast } = ToastStatusGlobal();

  const process = useCallback(
    (usageStatementId: string, payload: ProcessExemptionCopayPayload) => {
      let messageError = 'Erro ao processar isenção de coparticipação.';

      switch (payload.approve) {
        case false:
          messageError = 'Erro ao recusar isenção de coparticipação.';
          break;
        case null:
          messageError = 'Erro ao retomar isenção de coparticipação.';
          break;
      }

      return tryOrCatchMessageError(
        async () => {
          await copayService.process(usageStatementId, payload);

          const usageStatementProcess =
            await copayService.getOneUsageStatementProcess(usageStatementId);
          options?.callbackProcessSuccess?.(payload, usageStatementProcess);
        },
        {
          tryBefore: () => setLoading(true),
          tryAfter: () => setLoading(false),
          messageErrorDefault: messageError,
        },
      );
    },
    [options],
  );

  const publish = useCallback(
    (usageStatementId: string) => {
      return tryOrCatchMessageError(
        async () => {
          await copayService.publish(usageStatementId);
          options?.callbackPublishSuccess?.();
        },
        {
          tryBefore: () => setLoading(true),
          tryAfter: () => setLoading(false),
          messageErrorDefault: 'Erro ao enviar arquivo para faturamento',
        },
      );
    },
    [options],
  );

  const update = useCallback(
    (usageStatementId: string, payload: Partial<UsageStatement>) => {
      return tryOrCatchMessageError(
        async () => {
          await copayService.update(usageStatementId, payload);
          options?.callbackUpdateSuccess?.();
          toast('Feito', 'Arquivo atualizado.', 'success');
        },
        {
          tryBefore: () => setLoading(true),
          tryAfter: () => setLoading(false),
          messageErrorDefault: 'Erro ao atualizar arquivo.',
        },
      );
    },
    [options, toast],
  );

  return {
    loading,
    process,
    publish,
    update,
  };
};
